/**
 * @file VersionStructure.tsx
 * @description Структура страницы при «Посмотреть» версию (phase19.16 Р3а;
 * uiux-r3 §3.1 «Править в этом режиме нельзя»; QA Р3а D15): блоки снимка
 * версии (`GET pages/:id/revisions/:version`) только для чтения — без
 * перетаскивания, меню и меток правок, с подписью «Версия N». Скрытый в
 * версии блок — «скрыт».
 */

import { sitePageSectionTitle } from "@chaiberi/shared";
import { EyeOff, LayoutPanelTop } from "lucide-react";
import { useEffect, useState } from "react";

import { Skeleton } from "../../ui/skeleton";
import { sitePagePublish, type SitePageRevisionDetail } from "../../../lib/api-site-page-publish";
import { pagePublishErrorText } from "../../../lib/site-builder/page-publish-errors";

export function VersionStructure({ pageId, version }: { pageId: string; version: number }) {
  const [detail, setDetail] = useState<SitePageRevisionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    sitePagePublish
      .revision(pageId, version)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(pagePublishErrorText(err, "Не удалось загрузить блоки версии. Черновик и сайт в порядке."));
      });
    return () => {
      cancelled = true;
    };
  }, [pageId, version]);

  return (
    <div className="flex flex-col gap-1">
      <p className="type-caption px-1 pb-1 text-on-surface-variant">Версия {version} — только просмотр. Править в этом режиме нельзя.</p>
      {error ? <p className="type-caption px-1 text-on-surface-variant">{error}</p> : null}
      {!detail && !error ? <Skeleton className="h-24 rounded-lg" aria-label="Загружаем блоки версии" /> : null}
      {detail ? (
        detail.snapshot.sections.length === 0 ? (
          <p className="type-caption px-3 py-2 text-on-surface-variant">В этой версии блоков не было.</p>
        ) : (
          <ul aria-label={`Блоки версии ${version}`} className="flex flex-col gap-0.5">
            {detail.snapshot.sections.map((section, i) => (
              <li key={section.key} className="flex min-h-12 min-w-0 items-center gap-2 rounded-lg px-3 text-on-surface">
                <LayoutPanelTop aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
                <span className="type-body min-w-0 flex-1 break-words">{sitePageSectionTitle(section, i + 1)}</span>
                {section.isEnabled ? null : (
                  <span className="type-caption inline-flex shrink-0 items-center gap-1 text-on-surface-variant">
                    <EyeOff aria-hidden className="size-3.5" />
                    скрыт
                  </span>
                )}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
